import React, { useMemo } from "react";
import { PatientMoodEntry } from "../../types/api";
import { MOOD_ORDER, getMoodConfig } from "../../constants/mood";

interface MoodDistributionCardProps {
  entries: PatientMoodEntry[];
}

/**
 * Card 2 - Mood Distribution.
 * For each mood shows emoji, label, response count, percentage of total and a
 * horizontal progress bar; the footer reports the total number of responses.
 */
export const MoodDistributionCard: React.FC<MoodDistributionCardProps> = ({ entries }) => {
  const { counts, total } = useMemo(() => {
    const c: Record<string, number> = {};
    entries.forEach((e) => {
      c[e.mood] = (c[e.mood] || 0) + 1;
    });
    return { counts: c, total: entries.length };
  }, [entries]);

  return (
    <div className="border border-gray-200 rounded-lg bg-white p-4" dir="rtl">
      <h3 className="text-lg font-bold text-gray-900 mb-3">התפלגות מצב רוח</h3>

      {total === 0 ? (
        <div className="text-center text-xs text-gray-400 italic py-6">
          אין נתוני מצב רוח להצגה
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {MOOD_ORDER.map((mood) => {
              const config = getMoodConfig(mood)!;
              const count = counts[mood] || 0;
              const pct = total > 0 ? (count / total) * 100 : 0;
              return (
                <div key={mood}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="inline-flex items-center gap-2 text-xs font-medium text-gray-700">
                      <span className="text-2xl leading-none">{config.emoji}</span>
                      <span>{config.label}</span>
                    </span>
                    <span className="text-xs text-gray-600">
                      <span className="font-bold" style={{ color: config.color }}>
                        {count} פעמים
                      </span>{" "}
                      <span className="text-gray-400">({pct.toFixed(1)}%)</span>
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, backgroundColor: config.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-600">
            סך הכל תגובות: <span className="font-bold text-gray-900">{total}</span>
          </div>
        </>
      )}
    </div>
  );
};
